import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import HistoryIcon from '@mui/icons-material/History';
import dayjs from 'dayjs';
import { PoliciesService } from 'services';
import { useParams } from 'react-router-dom';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';
import { VersionComparisonDialog } from './VersionComparisonDialog';

type ChangeType = 'added' | 'removed' | 'unchanged';

interface ContentSection {
  id: string;
  type: ChangeType;
  text: string;
}

interface Version {
  version: string;
  isCurrent: boolean;
  releasedAt: string;
  releasedBy: string;
  notes: string;
  previousVersion: string | null;
  previousReleasedAt: string | null;
  previousContent: string | null;
  previousNotes: string | null;
  changes?: Array<{
    type: ChangeType;
    text: string;
  }>;
  summary?: {
    added: number;
    removed: number;
    unchanged: number;
  };
}

interface PolicyVersionData {
  policyId: string;
  versions: Version[];
}

const PolicyVersions = (): ReactElement => {
  const { policyId } = useParams<{ policyId: string }>();
  const [policyDetail, setPolicyDetail] = useState<PolicyVersionData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<{
    previous: { label: string; date: string };
    current: { label: string; date: string; isCurrent: boolean };
    sections: ContentSection[];
    summary: string;
    versionNotes: Array<{ version: string; isCurrent?: boolean; note: string }>;
  } | null>(null);

  const loadPolicyVersion = (): void => {
    if (!policyId) return;
    setIsLoading(true);
    PoliciesService.getPolicyVersion(policyId) // 69aa4862c69dbfe9c3e7fdf0 for testing
      .then((response) => {
        console.log(response);
        const { data } = response.data;
        setPolicyDetail(data);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  const capitalized = (word: string): string =>
    word
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' ');

  const handleViewDifferences = (
    version: Version,
    previousVersion: Version
  ): void => {
    const sections: ContentSection[] =
      version.changes?.map((change, idx) => ({
        id: `${change.type}-${idx}`,
        type: change.type,
        text: change.text,
      })) || [];

    // Create summary text
    const summaryText = version.summary
      ? `${version.summary.added} sections added, ${version.summary.removed} sections removed, ${version.summary.unchanged} sections unchanged`
      : 'No changes available';

    // Create version notes from all versions
    const versionNotes =
      policyDetail?.versions.map((v) => ({
        version: `Version ${v.version}`,
        isCurrent: v.isCurrent,
        note: v.notes,
      })) || [];

    setSelectedVersions({
      previous: {
        label: `Version ${previousVersion.version}`,
        date: dayjs(previousVersion.releasedAt).format('DD/MM/YYYY'),
      },
      current: {
        label: `Version ${version.version}`,
        date: dayjs(version.releasedAt).format('DD/MM/YYYY'),
        isCurrent: version.isCurrent,
      },
      sections,
      summary: summaryText,
      versionNotes,
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (policyId) {
      loadPolicyVersion();
    }
  }, [policyId]);

  return (
    <>
      <Box className="mt-5">
        <Typography variant="h3">Version History</Typography>

        {isLoading && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoading && policyDetail?.versions.length === 0 && (
          <Box className="empty-content">
            <Typography>No version history added</Typography>
          </Box>
        )}
        {!isLoading &&
          policyDetail?.versions.map((version, index) => {
            const previousVersion =
              index < policyDetail.versions.length - 1
                ? policyDetail.versions[index + 1]
                : null;
            const showViewDiff =
              previousVersion !== null &&
              version.changes &&
              version.changes.length > 0;

            return (
              <Box key={version.version} className="flex" sx={{ mb: 3, mt: 2 }}>
                <HistoryIcon
                  sx={{
                    marginRight: '10px',
                  }}
                  fontSize="large"
                />
                <Box>
                  <Typography variant="h4">
                    Version {version.version}
                    {version.isCurrent && (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{
                          background: '#d1f27a',
                          color: '#06362b',
                          ml: 1,
                        }}
                      />
                    )}
                  </Typography>

                  <Typography variant="body1" sx={{ fontSize: '12px' }}>
                    Released by: {version.releasedBy} | Released at:{' '}
                    {dayjs(version.releasedAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Notes: {capitalized(version.notes || '')}
                  </Typography>

                  {!showViewDiff && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 1,
                      }}
                    >
                      {version.previousVersion === null
                        ? 'Initial version'
                        : 'No changes to display'}
                    </Typography>
                  )}

                  {showViewDiff && previousVersion && (
                    <AppPrimaryButtonOutlined
                      variant="outlined"
                      className="btn-extra-small mt-2"
                      title="View Differences"
                      onClick={() =>
                        handleViewDifferences(version, previousVersion)
                      }
                    />
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>

      {selectedVersions && (
        <VersionComparisonDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          previous={selectedVersions.previous}
          current={selectedVersions.current}
          sections={selectedVersions.sections}
          summary={selectedVersions.summary}
          versionNotes={selectedVersions.versionNotes}
        />
      )}
    </>
  );
};

export default PolicyVersions;
